import { APP } from '../enums';
import { BrandData, TextGroup } from '../@types';
import { isConnect } from '../utils/isConnect';

export const isFeatureOverrideEnabled = (
  brandSettings: BrandData | null | undefined,
  featureName: string
): boolean =>
  brandSettings?.mobileFeatureOverrides?.includes(featureName) === true;

export const isBrand = (xbrandId?: string | null | undefined) =>
  !!xbrandId && xbrandId.length;

// Map xBrandId to fleetStyle (the short brand key used for map pins, etc.)
// fleetStyle values match what the API returns on car objects
export const xBrandIdToFleetStyle: Record<string, string> = {
  // UBQ
  [APP.BRAND.UBEEQO]: 'ubq',
  // MBA
  [APP.BRAND.MBA_STAGING]: 'mba',
  [APP.BRAND.MBA_PRODUCTION]: 'mba',
  // MIS
  [APP.BRAND.MIS]: 'mis',
  // BUB
  [APP.BRAND.BUB_STAGING]: 'bub',
  [APP.BRAND.BUB_PRODUCTION]: 'bub',
  // EPC (map-pin identity only — EPC has no area fleetStyle)
  [APP.BRAND.EPC_STAGING]: 'epc',
  [APP.BRAND.EPC_PRODUCTION]: 'epc',
};

// Get fleetStyle from xBrandId
export const getFleetStyleFromXBrandId = (
  xBrandId: string | undefined | null
): string | undefined => {
  if (!xBrandId) return undefined;
  return xBrandIdToFleetStyle[xBrandId];
};

// For now all Brands are forcing de, will keep the function for any future changes
export const getBrandLanguage = (xBrandId: string): string =>
  xBrandId
    ? isBND() || isTLA() || isFRM() || isMVV() || isGRF() || isBUB()
      ? 'deformal'
      : 'de'
    : 'de';

export const getBrandName = (xbrandId: string): string => {
  if (xbrandId) {
    if (xbrandId === '000000000000000000000002') {
      return 'ubq';
    }
    if (xbrandId === '000000000000000000000003') {
      return 'rw';
    }
    if (
      xbrandId === '6329b7e9448df21336278b69' ||
      xbrandId === '631b10eb46d0600e4545bb18'
    ) {
      return 'mba';
    }
    if (
      xbrandId === '66d47bb5d760865f0f1ec06a' ||
      xbrandId === '64215421fa9269520df3aad2' ||
      xbrandId === '6425a673a90bb10f63678c28'
    ) {
      return 'bnd';
    }
    if (xbrandId === '5ef1f2e4c8f48f0f7912d487') {
      return 'msi';
    }

    if (
      xbrandId === '64a44cc5d7a31b6ffe6628ed' ||
      xbrandId === '665eea6c3a906e06ecc5d306'
    ) {
      return 'tla';
    }
    if (
      xbrandId === '664e14bd4543650cd14422e9' ||
      xbrandId === '664f63166420fb076bbd2500'
    ) {
      return 'ctf';
    }
    if (
      xbrandId === '67224e0b93cfd812145b8cb4' ||
      xbrandId === '67225e21ec3cde06ec91240e'
    ) {
      return 'grf';
    }
    if (
      xbrandId === '69bc40d20cbdc405ce979c26' ||
      xbrandId === '69bc40d20cbdc405ce979c26'
    ) {
      return 'flo';
    }
    if (
      xbrandId === '667c2f40a4c9af5af7b7d529' ||
      xbrandId === '667d6383ea8db206e72d8148'
    ) {
      return 'sps';
    }
    if (
      xbrandId === '65771a4bc9944c3d1f0befcc' ||
      xbrandId === '6579aa3d3aadc607bd9674ae'
    ) {
      return 'frm';
    }
    if (
      xbrandId === '67031429d2e19b50c05b3ac5' ||
      xbrandId === '6705641eaa354b06daa69152'
    ) {
      return 'mvv';
    }
    if (
      xbrandId === '67fba816fc8fc50eec676c5f' ||
      xbrandId === '67f5445066dd81070e18995b'
    ) {
      return 'osn';
    }
    if (
      xbrandId === '6893a3b22fd49e6ad07e03cb' ||
      xbrandId === '6899d4c52438542de7a40026'
    ) {
      return 'sct';
    }
    if (
      xbrandId === '68db99e99c525855188c70a1' ||
      xbrandId === '68d2b8cdfc85d1466c5a0a29'
    ) {
      return 'bub';
    }
    if (
      xbrandId === '6a8ee1b65e3d7c01576f6bf7' ||
      xbrandId === '6a96f47d1b150f66bceda2eb'
    ) {
      return 'epc';
    }
    return '';
  }
  return 'getaway';
};

export const getBrandDomain = (xbrandId: string, bundleId: string): string => {
  const isStaging = bundleId.includes('staging');
  if (xbrandId) {
    if (xbrandId === '000000000000000000000002') {
      return isStaging
        ? 'https://staging.d2ywj8us3qqxbu.amplifyapp.com'
        : 'https://web.ubq.app';
    }
    if (xbrandId === '5ef1f2e4c8f48f0f7912d487') {
      return isStaging
        ? 'https://staging.d2pvvlsn034197.amplifyapp.com'
        : 'https://web.mietsmart.app';
    }
    if (xbrandId === '6329b7e9448df21336278b69') {
      return 'https://staging.d14n3yps53yo6s.amplifyapp.com';
    }
    if (xbrandId === '631b10eb46d0600e4545bb18') {
      return 'https://web.mietbar.app';
    }
    if (xbrandId === '68db99e99c525855188c70a1') {
      return 'https://staging.dbrdmyrgfvhzj.amplifyapp.com';
    }
    if (xbrandId === '68d2b8cdfc85d1466c5a0a29') {
      return 'https://web.buchbinder.app';
    }
    if (xbrandId === '6a8ee1b65e3d7c01576f6bf7') {
      return 'https://staging.d3emyfgvzvwuo.amplifyapp.com';
    }
    if (xbrandId === '6a96f47d1b150f66bceda2eb') {
      return 'https://web.europcar.app';
    }
  }
};

export const DEFAULT_PASSWORD_REGEX =
  /^(?=(?=.*?[A-Z])|(?=.*?[0-9])|(?=.*?[#?!@$%^&*-]))(?=.*?[a-z]).{8,}$/ as any;

/**
 * The colour each area brand re-skins to. The fleet shapes under
 * `web/public/svg/fleet*.svg` are drawn in whatever this returns, so adding a
 * style is an entry here plus its logo — no new artwork.
 */
export const FLEET_STYLE_COLORS: Record<string, string> = {
  obi: '#ff7e21',
  expert: '#FF5E19',
  spp: '#2d2d2d',
  bub: '#e3000f',
  zhero: '#36D1E8',
  jysk: '#143C8A',
  luckyCar: '#FFE500',
};

/** The area brand's colour, or undefined when the app is in its own colours. */
export const getFleetColor = (fleetStyle?: string | null): string | undefined =>
  fleetStyle ? FLEET_STYLE_COLORS[fleetStyle] : undefined;

export const getBrandColor = (fleetStyle = null) => {
  // Fleet style colors take priority over brand
  const fleetColor = getFleetColor(fleetStyle);
  if (fleetColor) return fleetColor;

  switch (process.env.VITE_X_BRAND_ID) {
    case '000000000000000000000003':
      return '#0077BE';
    case '000000000000000000000002':
      return '#00c4d1';
    case '5ef1f2e4c8f48f0f7912d487':
      return '#fac500';
    case '6329b7e9448df21336278b69':
    case '631b10eb46d0600e4545bb18':
      return '#1a6c7a';
    case '66d47bb5d760865f0f1ec06a':
    case '64215421fa9269520df3aad2':
    case '6425a673a90bb10f63678c28':
      return '#bb1b10';
    case '64a44cc5d7a31b6ffe6628ed':
    case '665eea6c3a906e06ecc5d306':
      return '#2E3080';
    case '65771a4bc9944c3d1f0befcc':
    case '6579aa3d3aadc607bd9674ae':
      return '#009EE2';
    case '67031429d2e19b50c05b3ac5':
    case '6705641eaa354b06daa69152':
      return '#0049ec';
    case '664e14bd4543650cd14422e9':
    case '664f63166420fb076bbd2500':
    case '667c2f40a4c9af5af7b7d529':
    case '667d6383ea8db206e72d8148':
      return '#75b42b';
    case '67224e0b93cfd812145b8cb4':
    case '67225e21ec3cde06ec91240e':
      return '#82ba26';
    case '69bc40d20cbdc405ce979c26':
      return '#31b7bc';
    case '67fba816fc8fc50eec676c5f':
    case '67f5445066dd81070e18995b':
      return '#1462a9';
    case '6893a3b22fd49e6ad07e03cb':
    case '6899d4c52438542de7a40026':
      return '#ff5c00';
    case '68db99e99c525855188c70a1':
    case '68d2b8cdfc85d1466c5a0a29':
      return '#e3000f';
    case '6a8ee1b65e3d7c01576f6bf7':
    case '6a96f47d1b150f66bceda2eb':
      return '#009900';
    default:
      return '#a2ad00';
  }
};

export const getSecondaryBrandColor = () => {
  switch (process.env.VITE_X_BRAND_ID) {
    case '000000000000000000000003':
    case '000000000000000000000002':
    case '65771a4bc9944c3d1f0befcc':
    case '6579aa3d3aadc607bd9674ae':
    case '664e14bd4543650cd14422e9':
    case '67224e0b93cfd812145b8cb4':
    case '67225e21ec3cde06ec91240e':
    case '664f63166420fb076bbd2500':
    case '667c2f40a4c9af5af7b7d529':
    case '667d6383ea8db206e72d8148':
    case '67031429d2e19b50c05b3ac5':
    case '6705641eaa354b06daa69152':
    case '67fba816fc8fc50eec676c5f':
    case '67f5445066dd81070e18995b':
    case '6893a3b22fd49e6ad07e03cb':
    case '6899d4c52438542de7a40026':
    case '68db99e99c525855188c70a1':
    case '68d2b8cdfc85d1466c5a0a29':
    case '6a8ee1b65e3d7c01576f6bf7':
    case '6a96f47d1b150f66bceda2eb':
    case '69bc40d20cbdc405ce979c26':
      return 'white';
    case '5ef1f2e4c8f48f0f7912d487':
      return 'rgba(0,0,0,0.5)';
    case '6329b7e9448df21336278b69':
    case '631b10eb46d0600e4545bb18':
    case '64a44cc5d7a31b6ffe6628ed':
    case '665eea6c3a906e06ecc5d306':
      return 'white';
    default:
      return 'white';
  }
};

export const getLightBrandColor = (fleetStyle = null): string => {
  // Fleet style colors take priority over brand
  if (fleetStyle === 'obi' || fleetStyle === 'expert')
    return 'rgba(255,126, 33,0.1)';
  if (fleetStyle === 'spp') return 'rgba(45,45, 45,0.1)';
  if (fleetStyle === 'bub') return 'rgba(227,0, 15,0.1)';
  if (fleetStyle === 'zhero') return 'rgba(54, 209, 232,0.1)';
  if (fleetStyle === 'jysk') return 'rgba(20, 60, 138,0.1)';
  if (fleetStyle === 'luckyCar') return 'rgba(255, 229, 0,0.1)';

  switch (process.env.VITE_X_BRAND_ID) {
    case '000000000000000000000002':
      return 'rgba(0, 196, 209, 0.13)';
    case '000000000000000000000003':
      return 'rgba(0, 119, 190, 0.13)';
    case '5ef1f2e4c8f48f0f7912d487':
      return 'rgba(250, 197, 0, 0.13)';
    case '6329b7e9448df21336278b69':
    case '631b10eb46d0600e4545bb18':
      return 'rgba(26, 108, 122,0.1)';
    case '66d47bb5d760865f0f1ec06a':
    case '64215421fa9269520df3aad2':
    case '6425a673a90bb10f63678c28':
      return 'rgba(255, 0, 0,0.1)';
    case '64a44cc5d7a31b6ffe6628ed':
    case '665eea6c3a906e06ecc5d306':
      return 'rgba(46,48,128,0.1)';
    case '65771a4bc9944c3d1f0befcc':
    case '6579aa3d3aadc607bd9674ae':
    case '67031429d2e19b50c05b3ac5':
    case '6705641eaa354b06daa69152':
      return 'rgba(0, 158, 226,0.1)';
    case '68db99e99c525855188c70a1':
    case '68d2b8cdfc85d1466c5a0a29':
      return 'rgba(227, 0, 15,0.1)';
    case '6a8ee1b65e3d7c01576f6bf7':
    case '6a96f47d1b150f66bceda2eb':
      return 'rgba(0, 153, 0,0.1)';
    case '664e14bd4543650cd14422e9':
    case '664f63166420fb076bbd2500':
      return 'rgba(117, 180, 43,0.1)';
    case '667c2f40a4c9af5af7b7d529':
    case '667d6383ea8db206e72d8148':
    case '67224e0b93cfd812145b8cb4':
    case '67225e21ec3cde06ec91240e':
    case '67fba816fc8fc50eec676c5f':
    case '6893a3b22fd49e6ad07e03cb':
    case '6899d4c52438542de7a40026':
    case '67f5445066dd81070e18995b':
      return 'rgba(0, 158, 226,0.1)';
    case '69bc40d20cbdc405ce979c26':
      return 'rgba(49, 183, 188,0.1)';
    default:
      return 'rgba(0, 119, 190, 0.13)';
  }
};

export const getVersionHeaderColor = (): string => {
  switch (process.env.VITE_X_BRAND_ID) {
    case '000000000000000000000002':
    case '000000000000000000000003':
    case '5ef1f2e4c8f48f0f7912d487':
      return 'white';
    case '6329b7e9448df21336278b69':
    case '631b10eb46d0600e4545bb18':
    case '66d47bb5d760865f0f1ec06a':
    case '64215421fa9269520df3aad2':
    case '6425a673a90bb10f63678c28':
    case '64a44cc5d7a31b6ffe6628ed':
    case '665eea6c3a906e06ecc5d306':
      return 'grey';
    case '65771a4bc9944c3d1f0befcc':
    case '6579aa3d3aadc607bd9674ae':
    case '67031429d2e19b50c05b3ac5':
    case '6705641eaa354b06daa69152':
    case '67fba816fc8fc50eec676c5f':
    case '6893a3b22fd49e6ad07e03cb':
    case '67f5445066dd81070e18995b':
    case '68db99e99c525855188c70a1':
    case '68d2b8cdfc85d1466c5a0a29':
    case '6a8ee1b65e3d7c01576f6bf7':
    case '6a96f47d1b150f66bceda2eb':
    case '664e14bd4543650cd14422e9':
    case '664f63166420fb076bbd2500':
    case '667c2f40a4c9af5af7b7d529':
    case '667d6383ea8db206e72d8148':
    case '67224e0b93cfd812145b8cb4':
    case '67225e21ec3cde06ec91240e':
    case '69bc40d20cbdc405ce979c26':
      return 'black';
    default:
      return 'rgba(0, 119, 190, 0.13)';
  }
};

export const getBrandTariffBackgroundColor = (fleetStyle = null) => {
  // Fleet style colors take priority over brand
  if (fleetStyle === 'obi') return '#ff7e21';
  if (fleetStyle === 'expert') return '#FF5E19';
  if (fleetStyle === 'spp') return '#2d2d2d';
  if (fleetStyle === 'bub') return '#e3000f';
  if (fleetStyle === 'zhero') return '#36D1E8';
  if (fleetStyle === 'jysk') return '#143C8A';
  if (fleetStyle === 'luckyCar') return '#FFE500';

  switch (process.env.VITE_X_BRAND_ID) {
    case '000000000000000000000003':
      return 'rgba(0, 119, 190, 0.1)';
    case '000000000000000000000002':
      return 'rgba(0, 196, 209, 0.1)';
    case '5ef1f2e4c8f48f0f7912d487':
      return 'rgba(250, 197, 0, 0.1)';
    case '6329b7e9448df21336278b69':
    case '631b10eb46d0600e4545bb18':
      return 'rgba(26, 108, 122, 0.1)';
    case '66d47bb5d760865f0f1ec06a':
    case '64215421fa9269520df3aad2':
    case '6425a673a90bb10f63678c28':
      return 'rgba(187, 27, 16, 0.1)';
    case '64a44cc5d7a31b6ffe6628ed':
    case '665eea6c3a906e06ecc5d306':
      return 'rgba(46, 48, 128, 0.1)';
    case '65771a4bc9944c3d1f0befcc':
    case '6579aa3d3aadc607bd9674ae':
      return 'rgba(0, 158, 226, 0.1)';
    case '67031429d2e19b50c05b3ac5':
    case '6705641eaa354b06daa69152':
      return 'rgba(0, 73, 236, 0.1)';
    case '664e14bd4543650cd14422e9':
    case '664f63166420fb076bbd2500':
    case '667c2f40a4c9af5af7b7d529':
    case '667d6383ea8db206e72d8148':
      return 'rgba(117, 180, 43, 0.1)';
    case '67224e0b93cfd812145b8cb4':
    case '67225e21ec3cde06ec91240e':
      return 'rgba(130, 186, 38, 0.1)';
    case '69bc40d20cbdc405ce979c26':
      return 'rgba(49, 183, 188, 0.1)';
    case '67fba816fc8fc50eec676c5f':
    case '67f5445066dd81070e18995b':
      return 'rgba(20, 98, 169, 0.1)';
    case '6893a3b22fd49e6ad07e03cb':
    case '6899d4c52438542de7a40026':
      return 'rgba(255, 92, 0, 0.1)';
    case '68db99e99c525855188c70a1':
    case '68d2b8cdfc85d1466c5a0a29':
      return 'rgba(227, 0, 15, 0.1)';
    case '6a8ee1b65e3d7c01576f6bf7':
    case '6a96f47d1b150f66bceda2eb':
      return 'rgba(0, 153, 0, 0.1)';
    default:
      return 'rgba(162, 173, 0, 0.1)';
  }
};

export const getBrandImagePath = (imageName: string): string => {
  if (process.env.VITE_THEME) {
    return `/images/${process.env.VITE_THEME?.toLowerCase()}/${imageName}.png`;
  }
  return `/images/msm/${imageName}.png`;
};

export const isROB = (): boolean =>
  process.env.VITE_X_BRAND_ID === '000000000000000000000003';

export const isUBQ = (): boolean =>
  process.env.VITE_X_BRAND_ID === '000000000000000000000002';

export const isMSM = (): boolean =>
  process.env.VITE_X_BRAND_ID === '5ef1f2e4c8f48f0f7912d487';

export const isTLA = (): boolean =>
  process.env.VITE_X_BRAND_ID === '64a44cc5d7a31b6ffe6628ed' ||
  process.env.VITE_X_BRAND_ID === '665eea6c3a906e06ecc5d306';

export const isCTF = (): boolean =>
  process.env.VITE_X_BRAND_ID === '664e14bd4543650cd14422e9' ||
  process.env.VITE_X_BRAND_ID === '664f63166420fb076bbd2500';

export const isGRF = (): boolean =>
  process.env.VITE_X_BRAND_ID === '67224e0b93cfd812145b8cb4' ||
  process.env.VITE_X_BRAND_ID === '67225e21ec3cde06ec91240e';

export const isFLO = (): boolean =>
  process.env.VITE_X_BRAND_ID === '69bc40d20cbdc405ce979c26' ||
  process.env.VITE_X_BRAND_ID === '69bc40d20cbdc405ce979c26';

export const isFRM = (): boolean =>
  process.env.VITE_X_BRAND_ID === '65771a4bc9944c3d1f0befcc' ||
  process.env.VITE_X_BRAND_ID === '6579aa3d3aadc607bd9674ae';

export const isMVV = (): boolean =>
  process.env.VITE_X_BRAND_ID === '67031429d2e19b50c05b3ac5' ||
  process.env.VITE_X_BRAND_ID === '6705641eaa354b06daa69152';

export const isOSN = (): boolean =>
  process.env.VITE_X_BRAND_ID === '67fba816fc8fc50eec676c5f' ||
  process.env.VITE_X_BRAND_ID === '67f5445066dd81070e18995b';

export const isSCT = (): boolean =>
  process.env.VITE_X_BRAND_ID === '6893a3b22fd49e6ad07e03cb' ||
  process.env.VITE_X_BRAND_ID === '6899d4c52438542de7a40026';

export const isBUB = (): boolean =>
  process.env.VITE_X_BRAND_ID === '68db99e99c525855188c70a1' ||
  process.env.VITE_X_BRAND_ID === '68d2b8cdfc85d1466c5a0a29';

export const isEPC = (): boolean =>
  process.env.VITE_X_BRAND_ID === '6a8ee1b65e3d7c01576f6bf7' ||
  process.env.VITE_X_BRAND_ID === '6a96f47d1b150f66bceda2eb';

export const isSPS = (): boolean =>
  process.env.VITE_X_BRAND_ID === '667c2f40a4c9af5af7b7d529' ||
  process.env.VITE_X_BRAND_ID === '667d6383ea8db206e72d8148';

export const isMBA = (): boolean =>
  process.env.VITE_X_BRAND_ID === '6329b7e9448df21336278b69' ||
  process.env.VITE_X_BRAND_ID === '631b10eb46d0600e4545bb18';

export const isMYM = (): boolean =>
  process.env.VITE_X_BRAND_ID === '65c902b020b9da363f47ac71' ||
  process.env.VITE_X_BRAND_ID === '65c4e44424b2c707891f4159';

export const isBND = (): boolean =>
  process.env.VITE_X_BRAND_ID === '64215421fa9269520df3aad2' ||
  process.env.VITE_X_BRAND_ID === '6425a673a90bb10f63678c28' ||
  process.env.VITE_X_BRAND_ID === '66d47bb5d760865f0f1ec06a';

export const isMymBrand = (xbrandId?: string | null | undefined) =>
  xbrandId === '65c902b020b9da363f47ac71' ||
  xbrandId === '65c4e44424b2c707891f4159';

export const isKYCApp = (): boolean => process.env.VITE_KYC_APP === 'true';

export const isV2BankFlow = (): boolean => true;

export const isServicesTabEnabled = () => isKYCApp();

const MOBILE_GROUPS = {
  [APP.BRAND.UBEEQO]: 'ubeeqo',
  [APP.BRAND.ROBBEN_WIENTJES]: 'robben-wientjes',
  [APP.BRAND.KONIG]: 'konig',
  [APP.BRAND.MBA_STAGING]: 'mietbar',
  [APP.BRAND.MBA_PRODUCTION]: 'mietbar',
  [APP.BRAND.MYM_STAGING]: 'my-motor',
  [APP.BRAND.MYM_PRODUCTION]: 'my-motor',
  [APP.BRAND.BUB_STAGING]: 'buchbinder',
  [APP.BRAND.BUB_PRODUCTION]: 'buchbinder',
  [APP.BRAND.EPC_STAGING]: 'europcar-gr',
  [APP.BRAND.EPC_PRODUCTION]: 'europcar-gr',
  [APP.BRAND.MIS]: 'miet-smart',
};

export const getBrandGroup = (xBrandId): TextGroup => {
  // Mobile
  if (MOBILE_GROUPS[xBrandId]) {
    return MOBILE_GROUPS[xBrandId] as TextGroup;
  }
  // Web
  if (isMSM()) return 'miet-smart';
  if (isBND()) return 'book-n-drive';
  if (isTLA()) return 'teilauto';
  if (isCTF()) return 'cityflitzer';
  if (isFRM()) return 'franklin-mobil';
  if (isMVV()) return 'mvv-fahrpack';
  if (isOSN()) return 'osnabrück';
  if (isSCT()) return 'scouter';
  if (isGRF()) return 'gruene-flotte';
  if (isFLO()) return 'flomobil';
  if (isSPS()) return 'sheepersharing';
  if (isBUB()) return 'buchbinder';
  if (isEPC()) return 'europcar-gr';
  if (isConnect()) return 'connect';
  return 'default';
};

// Get the current app's fleetStyle based on env (for web/mobile)
export const getAppFleetStyle = (): string => {
  if (isMBA()) return 'mba';
  if (isMSM()) return 'mis';
  if (isBUB()) return 'bub';
  if (isUBQ()) return 'ubq';
  return 'ubq';
};
